import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Container, CircularProgress, Alert, useTheme, useMediaQuery } from '@mui/material';
import ProfileForm from '../components/dashboard/ProfileForm';
import EUDRCredentialsForm from '../components/dashboard/EUDRCredentialsForm';
import APIKeysPanel from '../components/dashboard/APIKeysPanel';
import UsageBillingPanel from '../components/dashboard/UsageBillingPanel';
import InvoicesPaymentsPanel from '../components/dashboard/InvoicesPaymentsPanel';
import PricingPanel from '../components/dashboard/PricingPanel';
import NotificationSnackbar from '../components/common/NotificationSnackbar';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
}

function Dashboard() {
  const { user, refreshUser, isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [value, setValue] = useState(() => {
    const hash = window.location.hash.slice(1).toLowerCase();
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab')?.toLowerCase();

    const tabMap = {
      'profile': 0,
      'credentials': 1,
      'apikeys': 2,
      'usage': 3,
      'invoices': 4,
      'pricing': 5
    };

    return tabMap[hash] ?? tabMap[tabParam] ?? 0;
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const fetchAttempted = useRef(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    description: '',
    severity: 'success'
  });

  // Check URL for refresh parameter - this happens when returning from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('refresh') === 'true') {
      // Remove the parameter to prevent repeated refreshes
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
      
      // Refresh user data
      refreshUser().then(result => {
        if (result.success) {
          showNotification(
            'Subscription Updated',
            'Your subscription has been successfully updated.',
            'success'
          );
        }
      }).catch(err => {
        console.error('Error refreshing user data:', err);
      });
    }
  }, [refreshUser]);

  useEffect(() => {
    if (!isAuthenticated() || fetchAttempted.current) return;
    
    async function loadUserProfile() {
      setProfileLoading(true);
      fetchAttempted.current = true;
      
      try {
        // Always use refreshUser instead of fetchUserProfile to ensure the most up-to-date data
        const result = await refreshUser();
        if (!result.success) {
          setProfileError(result.error);
          showNotification(
            'Failed to Load Profile',
            result.error,
            'error'
          );
        }
      } catch (error) {
        setProfileError('Could not load user profile');
        showNotification(
          'Failed to Load Profile',
          error.response?.data?.message || 'Could not load user profile',
          'error'
        );
      } finally {
        setProfileLoading(false);
      }
    }
    
    loadUserProfile();
  }, [refreshUser, isAuthenticated]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const tabNames = ['profile', 'credentials', 'apikeys', 'usage', 'invoices', 'pricing'];
    window.history.pushState(null, '', `#${tabNames[newValue]}`);
  };

  const showNotification = (message, description, severity = 'success') => {
    setNotification({
      open: true,
      message,
      description,
      severity
    });
  };

  const handleNotificationClose = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  if (!isAuthenticated()) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Alert severity="error">
          You are not logged in. Please log in to access the dashboard.
        </Alert>
      </Container>
    );
  }

  if (profileLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (profileError && !user) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Alert severity="error">
          {profileError}
        </Alert>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ flex: 1 }}>
        <h1 className="font-bold mb-8">EUDR API Dashboard</h1>
        
        <Box sx={{ width: '100%' }}>
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
          }}>
            <Tabs 
              value={value} 
              onChange={handleChange} 
              aria-label="dashboard tabs"
              textColor="primary"
              indicatorColor="primary"
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              allowScrollButtonsMobile
              sx={{
                '.MuiTab-root': {
                  fontSize: isMobile ? '0.8rem' : 'inherit',
                  minWidth: isMobile ? 'auto' : 90,
                  px: isMobile ? 1.5 : 2,
                  minHeight: isMobile ? 48 : 56,
                }
              }}
            >
              <Tab label="Profile" {...a11yProps(0)} />
              <Tab label="Credentials" {...a11yProps(1)} />
              <Tab label="API Keys" {...a11yProps(2)} />
              <Tab label="Usage" {...a11yProps(3)} />
              <Tab label="Invoices" {...a11yProps(4)} />
              <Tab label="Pricing" {...a11yProps(5)} />
            </Tabs>
          </Box>

          <CustomTabPanel value={value} index={0}>
            <ProfileForm onNotification={showNotification} />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={1}>
            <EUDRCredentialsForm onNotification={showNotification} />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={2}>
            <APIKeysPanel onNotification={showNotification} />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={3}>
            <UsageBillingPanel onNotification={showNotification} />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={4}>
            <InvoicesPaymentsPanel onNotification={showNotification} />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={5}>
            <PricingPanel onNotification={showNotification} />
          </CustomTabPanel>
        </Box>
      </Container>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        description={notification.description}
        severity={notification.severity}
        onClose={handleNotificationClose}
      />
    </>
  );
}

export default Dashboard;
